import { MRT_ColumnDef, MRT_RowData } from "material-react-table";
import type { FilterSpec } from "../../hooks/useServerFilters";
import { GetTimestampAsDateTimeString } from "../PrimitiveUtils";
import {
  RichTextEditor,
  TABLE_READ_ONLY_MAX_LINES,
} from "../../components/field/richTextEditor/RichTextEditor";
import EntityReferenceComponent from "../../components/entitiesReferences/EntityReferenceComponent";
import { EntityProofreadingStatusChip } from "../../components/forms/common/status/EntityProofreadingStatusChip";
import {
  SystemEntityCollections,
  SystemEntityProofreadingStatus,
  SystemFieldProofreadingStatus,
} from "../../shared/enums/shared-enums";
import {
  NameInternalEvidence,
  NameExternalEvidence,
} from "../../types/nameEvidence";
import { VersionReference } from "../../types/version";
import { Box } from "@mui/material";
import { DISPLAY_CONFIG } from "../../config/displayConfig";
import { AttributesMetadata } from "../../types/attributeMetadata";
import { fieldStatusConfigs } from "../../components/forms/common/status/utils";
import "../../styles/EntityStatusCell.css";

export const formatArrayColumn = <T,>(
  value: T[] | undefined | null,
  mapper: (item: T) => string | undefined,
  filterFn: (item: T) => boolean = () => true,
  maxItems: number = DISPLAY_CONFIG.table.maxDisplayItems
): string => {
  if (!value || value.length === 0) return "-";
  const filtered = value.filter(filterFn);
  const formatted = filtered.map(mapper).filter((item): item is string => !!item);

  if (formatted.length === 0) return "-";
  if (formatted.length <= maxItems) return formatted.join(", ");

  const displayed = formatted.slice(0, maxItems);
  const remaining = formatted.length - maxItems;
  return `${displayed.join(", ")} (and ${remaining} more...)`;
};

export const modifiedAtColumn: MRT_ColumnDef<any> = {
  accessorKey: "modified_at",
  header: "Updated",
  Cell: ({ row }) =>
    row.original.modified_at ? (
      <div>{GetTimestampAsDateTimeString(row.original.modified_at)}</div>
    ) : (
      <div>-</div>
    ),
};

export const modifiedByColumn: MRT_ColumnDef<any> = {
  accessorKey: "modified_by",
  header: "Updated By",
  Cell: ({ row }) =>
    row.original.modified_by ? (
      <div>{row.original.modified_by}</div>
    ) : (
      <div>-</div>
    ),
};

export const nameColumn: MRT_ColumnDef<any> = {
  accessorKey: "name",
  header: "Name",
};

const getStatusClassName = (status: SystemEntityProofreadingStatus | undefined): string => {
  switch (status) {
    case SystemEntityProofreadingStatus.NOT_PROOFREAD:
      return 'entity-status-not-proofread';
    case SystemEntityProofreadingStatus.PARTLY_PROOFREAD:
      return 'entity-status-partly-proofread';
    case SystemEntityProofreadingStatus.UNPUBLISHED:
      return 'entity-status-unpublished';
    case SystemEntityProofreadingStatus.PUBLISHED:
      return 'entity-status-published';
    default:
      return 'entity-status-not-proofread';
  }
};

const getFieldStatusClassName = (status: SystemFieldProofreadingStatus | undefined): string => {
  switch (status) {
    case SystemFieldProofreadingStatus.NO_PROOFREADING:
      return 'entity-field-status-no-proofreading';
    case SystemFieldProofreadingStatus.NEEDS_WORK:
      return 'entity-field-status-needs-work';
    case SystemFieldProofreadingStatus.PROOFREAD_ONCE:
      return 'entity-field-status-proofread-once';
    case SystemFieldProofreadingStatus.PROOFREAD_TWICE:
      return 'entity-field-status-proofread-twice';
    case SystemFieldProofreadingStatus.APPROVED:
      return 'entity-field-status-approved';
    default:
      return '';
  }
};

type EntityWithAttributeMetadata = {
  attributes_metadata?: AttributesMetadata;
};

const getFieldProofreadingStatus = (
  entity: EntityWithAttributeMetadata,
  metadataFieldName: string
): SystemFieldProofreadingStatus | undefined =>
  entity.attributes_metadata?.[metadataFieldName]?.system_proofreading_status;

export const renderCellWithProofreadingIndicator = (
  entity: EntityWithAttributeMetadata,
  metadataFieldName: string,
  content: React.ReactNode
): React.ReactNode => {
  const status = getFieldProofreadingStatus(entity, metadataFieldName);

  if (!status) {
    return content;
  }

  const statusClassName = getFieldStatusClassName(status);
  const statusLabel = fieldStatusConfigs[status]?.label;
  const title = typeof statusLabel === "string" ? statusLabel : undefined;

  return (
    <div className={`entity-status-cell ${statusClassName}`} title={title}>
      {content}
    </div>
  );
};

export const withFieldProofreadingIndicator = (
  metadataFieldName: string,
  render: (props: any) => React.ReactNode,
  enabled: boolean = true
) => {
  if (!enabled) {
    return render;
  }

  return (props: any) =>
    renderCellWithProofreadingIndicator(
      props.row.original,
      metadataFieldName,
      render(props)
    );
};

export const publishedIdColumn: MRT_ColumnDef<any> = {
  accessorKey: "published_id",
  header: "Intellexus ID",
  Cell: ({ row }) => {
    const entity = row.original;
    const status = entity.entity_status_of_proofreading as SystemEntityProofreadingStatus;
    const statusClassName = getStatusClassName(status);

    if (!entity._id || !entity.published_id) {
      return (
        <div className={`entity-status-cell ${statusClassName}`}>
          {entity.published_id ?? "-"}
        </div>
      );
    }

    return (
      <div className={`entity-status-cell ${statusClassName}`}>
        <EntityReferenceComponent entity={entity} />
      </div>
    );
  },
};

export const versionRefColumn: MRT_ColumnDef<any> = {
  accessorKey: "Version",
  header: "Version",
  Cell: ({ row }) => (
    <EntityReferenceComponent
      entityCollection={SystemEntityCollections.VERSION}
      entity={row.original.Versions}
    />
  ),
};

export const notesColumn: MRT_ColumnDef<any> = {
  id: "notes",
  accessorFn: (row) => (row as any).notes ?? "-",
  header: "Notes",
  Cell: withFieldProofreadingIndicator("notes", ({ row }) => {
    return (
      <RichTextEditor
        value={row.original.notes}
        readOnly={true}
        maxLines={TABLE_READ_ONLY_MAX_LINES}
      />
    );
  }),
};

export const statusOfProofreadingColumn: MRT_ColumnDef<any> = {
  accessorKey: "entity_status_of_proofreading",
  header: "Proofreading",
  size: 120,
  Cell: ({ row }) => (
    <EntityProofreadingStatusChip
      status={
        row.original
          .entity_status_of_proofreading as SystemEntityProofreadingStatus
      }
      small
    />
  ),
};

export const EntityTableSuffixColumns: MRT_ColumnDef<any>[] = [
  notesColumn,
  modifiedAtColumn,
  modifiedByColumn,
];

export const EntityWithMetadataTableSuffixColumns: MRT_ColumnDef<any>[] = [
  notesColumn,
  statusOfProofreadingColumn,
  modifiedAtColumn,
  modifiedByColumn,
];

/**
 * Canonical ids of the two entity-reference columns every connection (edge)
 * column set exposes. Kept here so the column files and the table helpers that
 * reason about them share one definition.
 */
export const CONNECTION_FROM_COLUMN_ID = "fromDoc";
export const CONNECTION_TO_COLUMN_ID = "toDoc";

/**
 * Filter-spec entries that match the suffix columns above. Co-located with
 * the columns so the two cannot drift. Spread into each entity's filter spec
 * the same way the suffix columns are spread into its columns array.
 */
export const commonEntityFilterSpec: FilterSpec = {
  notes: { kind: "string" },
  modified_at: { kind: "string" },
  modified_by: { kind: "string" },
};

export const commonEntityWithMetadataFilterSpec: FilterSpec = {
  ...commonEntityFilterSpec,
  entity_status_of_proofreading: { kind: "string" },
};

export const publishedIdFilterSpec: FilterSpec = {
  published_id: { kind: "string" },
};

/**
 * Builds an entity filter spec by prepending publishedId and appending the
 * common "with metadata" suffix, mirroring how GetEntityTablePrefixColumns /
 * EntityWithMetadataTableSuffixColumns compose an entity's columns array.
 */
export const createEntityFilterSpec = (
  entrySpecs: FilterSpec
): FilterSpec => ({
  ...publishedIdFilterSpec,
  ...entrySpecs,
  ...commonEntityWithMetadataFilterSpec,
});

/**
 * Same as createEntityFilterSpec but without publishedId — for connection
 * (edge) entities, which don't have a published_id column.
 */
export const createConnectionFilterSpec = (
  entrySpecs: FilterSpec
): FilterSpec => ({
  ...entrySpecs,
  ...commonEntityWithMetadataFilterSpec,
});

export function createReversedMapping<T extends string, U extends string>(
  mapping: Record<T, U>
): Record<U, T> {
  return Object.fromEntries(
    Object.entries(mapping).map(([key, value]) => [value, key])
  ) as Record<U, T>;
}

export function GetEntityTableSuffixColumns<T extends MRT_RowData>(
  columns: MRT_ColumnDef<T>[]
): MRT_ColumnDef<T, any>[] {
  return [
    ...columns,
    notesColumn as MRT_ColumnDef<T>,
    statusOfProofreadingColumn as MRT_ColumnDef<T>,
    modifiedAtColumn as MRT_ColumnDef<T>,
    modifiedByColumn as MRT_ColumnDef<T>,
  ];
}

export function GetEntityTablePrefixColumns<T extends MRT_RowData>(
  columns: MRT_ColumnDef<T>[],
  useNameColumn: boolean = true,
  usePublishedIdColumn: boolean = true
): MRT_ColumnDef<T>[] {
  const prefixColumns: MRT_ColumnDef<T>[] = [];

  if (useNameColumn) {
    prefixColumns.push(nameColumn as MRT_ColumnDef<T>);
  }

  if (usePublishedIdColumn) {
    prefixColumns.push(publishedIdColumn as MRT_ColumnDef<T>);
  }

  return [...prefixColumns, ...columns];
}

export function extractNumericId(
  input: string | undefined,
  prefix: string
): number | undefined {
  try {
    if (!input || typeof input !== "string") return undefined;

    const cleaned = input.replace(prefix, "");
    const numericId = parseInt(cleaned, 10);

    if (isNaN(numericId)) {
      console.warn(
        `Invalid numeric ID after removing prefix '${prefix}' from '${input}'`
      );
      return undefined;
    }

    return numericId;
  } catch (err) {
    console.error(
      `Failed to extract numeric ID from '${input}' with prefix '${prefix}':`,
      err
    );
    return undefined;
  }
}

export const constructFullId = (
  collection: SystemEntityCollections,
  key: string | number
): string => `${collection}/${key}`;

export const VersionCell: React.FC<{ value: VersionReference }> = ({
  value,
}) => {
  if (!value) return <Box>-</Box>;

  return value.version_exist ? (
    <EntityReferenceComponent entity={value.version} />
  ) : value.version_notes ? (
    <RichTextEditor
      value={`Notes: \n ${value.version_notes}`}
      readOnly={true}
      maxLines={TABLE_READ_ONLY_MAX_LINES}
    />
  ) : (
    <Box>-</Box>
  );
};

export const formatEvidenceName = (
  evidence: NameInternalEvidence | NameExternalEvidence
): string => {
  if (!evidence) return "-";

  const { attribute_pre = "", name = "", attribute_post = "" } = evidence;
  const nameParts = [attribute_pre, name, attribute_post]
    .filter(Boolean)
    .join(" ");

  const evidenceType =
    "internal_evidence_type" in evidence
      ? evidence.internal_evidence_type
      : evidence.external_evidence_type;

  const typeSuffix = evidenceType ? ` (${evidenceType})` : "";

  return nameParts + typeSuffix;
};
